/**
 * When to ask somebody to turn desktop alerts on, and how to remember what they said (D-155).
 *
 * ⚠️ **A SOFT ASK, NEVER THE BROWSER'S DIALOG ON LOAD.** Gmail and Slack draw a bar of their
 * own — "Enable desktop notifications · Enable · No thanks" — and open the browser's permission
 * dialog only from the press on Enable. Two reasons, both of which bind here. The dialog can be
 * opened only from a real gesture, so a page that asks unprompted is refused; and Chrome scores
 * every site by how often its prompt is refused and quietly demotes one that scores badly,
 * hiding the prompt behind a bell icon for everybody who uses that site afterwards. A bar
 * somebody can decline costs nothing; a dialog somebody declines costs every future user.
 *
 * ⚠️ **THE CADENCE IS THE ASK: once a week for the first three weeks, then once a month.**
 * Dismissals one to three snooze it seven days each, so it is seen in weeks 0, 1, 2 and 3; from
 * the fourth on, thirty. Turning it on ends it. A browser-level refusal ends it too, because no
 * page can ask again after one — the permission itself says "denied" and the bar is never drawn.
 *
 * ⚠️ **PER DEVICE, IN `localStorage`, ON PURPOSE.** The browser's permission is per browser and
 * per machine, so the reminder about it is too: somebody who allowed it at their desk is asked
 * on the ward machine, where it is not yet allowed. Nothing is written server-side — no column,
 * no migration, no record of who was asked when — and clearing browser data restarts the cycle,
 * which is accepted. Safe in the way D1 was not: this decides whether a bar of copy is drawn,
 * never what anybody may do.
 *
 * ⚠️ **NOT ON ARRIVAL.** "While they are logged in and active" was the brief, and the bar is
 * the first thing a new user should not meet. It waits for the tab to have been in front for a
 * few minutes, or for a request to have been opened — whichever comes first — and that wait is
 * per session, so a reload does not reset a snooze and does not skip the wait either.
 */

export const DESKTOP_NUDGE_KEY = "ars-desktop-nudge";

/** How many dismissals are answered with a week's snooze before the cadence becomes monthly. */
export const WEEKLY_ASKS = 3;

export const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
export const MONTH_MS = 30 * 24 * 60 * 60 * 1000;

/** How long the tab has to have been in front before the bar may appear, per session. */
export const ACTIVE_BEFORE_ASK_MS = 3 * 60 * 1000;

export interface DesktopNudgeState {
  /** How many times it has been dismissed on this device. */
  dismissals: number;

  /** Not before this instant, as an ISO string. Absent means it may be shown. */
  snoozedUntil?: string;
}

export const EMPTY_NUDGE: DesktopNudgeState = { dismissals: 0 };

/**
 * Whether the bar may be shown now, given what was stored.
 *
 * Pure, so the cadence can be tested against a clock rather than waited for.
 */
export function mayAsk(state: DesktopNudgeState, now: Date): boolean {
  if (!state.snoozedUntil) return true;

  const until = Date.parse(state.snoozedUntil);
  if (Number.isNaN(until)) return true;

  return now.getTime() >= until;
}

/**
 * The state after a dismissal: one more on the count, and a snooze whose length depends on
 * how many times this device has already said no.
 */
export function dismissed(state: DesktopNudgeState, now: Date): DesktopNudgeState {
  const dismissals = state.dismissals + 1;
  const snooze = dismissals <= WEEKLY_ASKS ? WEEK_MS : MONTH_MS;

  return { dismissals, snoozedUntil: new Date(now.getTime() + snooze).toISOString() };
}

/**
 * The state after somebody switched desktop alerts OFF on their profile, deliberately.
 *
 * ⚠️ Counted straight onto the monthly cadence. A person who went to the settings and said no
 * has answered more clearly than one who closed a bar, and asking them again next week would
 * be the product arguing with a decision it was just told.
 */
export function optedOut(state: DesktopNudgeState, now: Date): DesktopNudgeState {
  return {
    dismissals: Math.max(state.dismissals, WEEKLY_ASKS),
    snoozedUntil: new Date(now.getTime() + MONTH_MS).toISOString(),
  };
}

export function readNudgeState(): DesktopNudgeState {
  try {
    const raw = localStorage.getItem(DESKTOP_NUDGE_KEY);
    if (!raw) return EMPTY_NUDGE;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return EMPTY_NUDGE;

    const record = parsed as Record<string, unknown>;
    const dismissals =
      typeof record.dismissals === "number" && Number.isFinite(record.dismissals)
        ? Math.max(0, Math.floor(record.dismissals))
        : 0;
    const snoozedUntil = typeof record.snoozedUntil === "string" ? record.snoozedUntil : undefined;

    return snoozedUntil ? { dismissals, snoozedUntil } : { dismissals };
  } catch {
    // Storage blocked or unreadable: treat as never asked. The worst outcome is one extra
    // bar, which is dismissable.
    return EMPTY_NUDGE;
  }
}

export function writeNudgeState(state: DesktopNudgeState): void {
  try {
    localStorage.setItem(DESKTOP_NUDGE_KEY, JSON.stringify(state));
  } catch {
    // Nothing to do. An unwritable snooze means the bar may come back sooner; it stays
    // dismissable.
  }
}
